import {
  getQuickFixesForIssue,
  type QuickFixConfig,
} from '../quickFixConfig';
import {
  getGuidedActionForIssue,
  type GuidedActionConfig,
} from './issueGuidedActions';
import { normalizeValidationId } from '../validationConstants';
import type { ValidationIssue } from '../types';

export interface ValidationDirectRepairOption {
  mode: 'direct';
  undoMode: 'available';
  label: string;
  description: string;
  validationId: number;
  quickFix: QuickFixConfig;
}

export interface ValidationGuidedRepairOption {
  mode: 'guided';
  undoMode: 'not_applicable';
  label: string;
  description: string;
  validationId: number;
  guidedAction: GuidedActionConfig;
}

export type ValidationRepairOption =
  | ValidationDirectRepairOption
  | ValidationGuidedRepairOption;

function isValueCoverageIssue(issue: ValidationIssue, upperMessage: string): boolean {
  if (normalizeValidationId(issue.ValidationId) !== 34) {
    return false;
  }

  return upperMessage.includes('HAS NO VALUES') || upperMessage.includes('IS EMPTY');
}

function isMappingConfigurationIssue(issue: ValidationIssue, upperMessage: string): boolean {
  const normalizedValidationId = normalizeValidationId(issue.ValidationId);

  if (normalizedValidationId !== 34) {
    return false;
  }

  if (isValueCoverageIssue(issue, upperMessage)) {
    return false;
  }

  return (
    upperMessage.includes('REQUIRED MAPPING') ||
    upperMessage.includes('MAPPED PARAMETER') ||
    upperMessage.includes('TARGET REVIT PARAMETER') ||
    upperMessage.includes('FORMULA') ||
    upperMessage.includes('NOT FOUND IN THIS MODEL') ||
    upperMessage.includes('NOT FOUND ON PIPING ELEMENTS')
  );
}

export function isDirectValidationRepairOption(
  option: ValidationRepairOption
): option is ValidationDirectRepairOption {
  return option.mode === 'direct';
}

export function isGuidedValidationRepairOption(
  option: ValidationRepairOption
): option is ValidationGuidedRepairOption {
  return option.mode === 'guided';
}

export function getValidationRepairOptions(issue: ValidationIssue): ValidationRepairOption[] {
  const upperMessage = (issue.Message || '').toUpperCase();
  const normalizedValidationId = normalizeValidationId(issue.ValidationId);
  const allowDirectFixes = !isMappingConfigurationIssue(issue, upperMessage);

  const directFixes = allowDirectFixes
    ? getQuickFixesForIssue(
        issue.ValidationId,
        issue.Message,
        issue.AdditionalData,
        issue.ElementIds.length
      )
    : [];

  if (directFixes.length > 0) {
    return directFixes.map((quickFix) => ({
      mode: 'direct',
      undoMode: 'available',
      label: quickFix.label,
      description: quickFix.description,
      validationId: normalizedValidationId,
      quickFix,
    }));
  }

  const guidedAction = getGuidedActionForIssue(issue);
  if (!guidedAction) {
    return [];
  }

  return [
    {
      mode: 'guided',
      undoMode: 'not_applicable',
      label: guidedAction.label,
      description: guidedAction.description,
      validationId: normalizedValidationId,
      guidedAction,
    },
  ];
}
